import {
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Select
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { FormInstance, RadioChangeEvent } from 'antd'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dateFormat } from '../../../utils/utils'
import { SupDatePicker } from '../../../styledComponents'
import { RuleObject } from 'antd/lib/form'
import dayjs from 'dayjs'
import { DolsStatus } from '@lmc/ts-common'
import { capitalize } from 'lodash'
import useFeatureFlags from 'hooks/useFeatureFlags'

const dolsStatuses = Object.values(DolsStatus)
const dolsStatusesOptions = [
  ...dolsStatuses.map((status) => ({
    label: capitalize(status),
    value: status
  })),
  { label: 'No Status', value: null }
]

type LpsFormCardProps = {
  dols: boolean
  isDoLSOverride: boolean
  form: FormInstance
}

export const LpsFormCard = ({
  dols,
  isDoLSOverride,
  form
}: LpsFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [showDateFields, setShowDateFields] = useState(dols)
  const dolsModsAlignment = useFeatureFlags({
    flagNames: ['dols_mods_alignment']
  }).data?.dols_mods_alignment.enabled

  const onRadioChange = (e: RadioChangeEvent) => {
    setShowDateFields(e.target.value === true)
  }

  const endDateValidator = async (
    _rule: RuleObject,
    endDate: dayjs.Dayjs
  ): Promise<void> => {
    const startDate = form.getFieldValue('dolsStartDate')
    if (!endDate || !startDate) {
      return
    }
    if (endDate.isBefore(startDate)) {
      throw new Error(t(supConstants.LPS_END_DATE_VALIDATION))
    }
  }

  useEffect(() => {
    // init dols / dolsStatus field based on the feature flag
    // should be removed with the feature flag deprecation
    if (dolsModsAlignment) {
      const dolsStatus: DolsStatus = form.getFieldValue('dolsStatus')
      form.setFieldValue('dols', dolsStatus === DolsStatus.authorised)
      form.setFieldValue('dolsStatus', dolsStatus || null)
    }
  }, [])

  const getOptionalDateFields = () => {
    return (
      <>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_APPLICATION_DATE)
                : t(supConstants.LPS_APPLICATION_DATE)}
            </Text>
          }
          name="dolsApplicationDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_START_DATE)
                : t(supConstants.LPS_START_DATE)}
            </Text>
          }
          name="dolsStartDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_END_DATE)
                : t(supConstants.LPS_END_DATE)}
            </Text>
          }
          name="dolsEndDate"
          rules={[
            {
              validator: endDateValidator,
              message: t(supConstants.LPS_END_DATE_VALIDATION)
            }
          ]}
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_REVIEW_DATE)
                : t(supConstants.LPS_REVIEW_DATE)}
            </Text>
          }
          name="dolsReviewDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
      </>
    )
  }

  const getRequiredDateFileds = () => {
    return (
      <>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_APPLICATION_DATE)
                : t(supConstants.LPS_APPLICATION_DATE)}
            </Text>
          }
          name="dolsApplicationDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_START_DATE)
                : t(supConstants.LPS_START_DATE)}
            </Text>
          }
          name="dolsStartDate"
          rules={[
            {
              required: true,
              message: `${t(supConstants.PLEASE_ENTER)} ${
                isDoLSOverride
                  ? t(supConstants.DOLS_START_DATE)
                  : t(supConstants.LPS_START_DATE)
              }`
            }
          ]}
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_END_DATE)
                : t(supConstants.LPS_END_DATE)}
            </Text>
          }
          name="dolsEndDate"
          rules={[
            {
              required: true,
              message: `${t(supConstants.PLEASE_ENTER)} ${
                isDoLSOverride
                  ? t(supConstants.DOLS_END_DATE)
                  : t(supConstants.LPS_END_DATE)
              }`
            },
            {
              validator: endDateValidator,
              message: t(supConstants.LPS_END_DATE_VALIDATION)
            }
          ]}
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_REVIEW_DATE)
                : t(supConstants.LPS_REVIEW_DATE)}
            </Text>
          }
          name="dolsReviewDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
      </>
    )
  }

  return (
    <FormCard
      title={isDoLSOverride ? t(supConstants.DOLS) : t(supConstants.LPS)}
      helpArticleUrl="https://support.logmycare.co.uk/en/articles/6534074-managing-lps-dols-on-client-profiles"
    >
      {dolsModsAlignment && (
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DEPRIVATION)
                : t(supConstants.LIBERTY_PROTECTION_SAFEGUARD)}
            </Text>
          }
          colon={false}
          name="dolsStatus"
        >
          <Select
            defaultValue={null}
            optionRender={(option) => <Text>{option.label}</Text>}
            options={dolsStatusesOptions}
            onChange={(value: DolsStatus) => {
              form.setFieldValue('dols', value === DolsStatus.authorised)
            }}
          />
        </FormItem>
      )}

      <FormItem
        noStyle={dolsModsAlignment}
        label={
          <Text>
            {isDoLSOverride
              ? t(supConstants.DEPRIVATION)
              : t(supConstants.LIBERTY_PROTECTION_SAFEGUARD)}
          </Text>
        }
        name="dols"
        colon={false}
      >
        {!dolsModsAlignment && (
          <FormRadioGroup onChange={onRadioChange}>
            <FormRadio value={false} text={t(supConstants.NO)} />
            <FormRadio value text={t(supConstants.YES)} />
          </FormRadioGroup>
        )}
      </FormItem>

      {dolsModsAlignment
        ? getOptionalDateFields()
        : showDateFields && getRequiredDateFileds()}
    </FormCard>
  )
}
